import React, { useId } from "react";

// Lightweight inline-SVG Sri Lankan art motifs. All use currentColor so they
// inherit theme colour from the parent's `color` / `style={{ color }}`.

// Kandyan liya-pata scroll divider — a flowing vine of paired spirals.
export const LiyaPataDivider = ({ className = "", style = {}, testid }) => (
  <svg
    className={`sk-liyapata ${className}`}
    data-testid={testid}
    viewBox="0 0 240 20"
    preserveAspectRatio="xMidYMid meet"
    fill="none"
    style={style}
    aria-hidden="true"
  >
    <path
      d="M0 10 C 20 -4, 40 24, 60 10 S 100 -4, 120 10 S 160 24, 180 10 S 220 -4, 240 10"
      stroke="currentColor"
      strokeWidth="1.6"
      strokelinecap="round"
    />
    {[20, 80, 140, 200].map((cx) => (
      <circle key={cx} cx={cx} cy="10" r="2.2" fill="currentColor" />
    ))}
    {[50, 110, 170, 230].map((cx) => (
      <path key={cx} d={`M${cx} 10 q 5 -6 10 0`} stroke="currentColor" strokeWidth="1.3" />
    ))}
  </svg>
);

// Eight-petalled lotus medallion.
export const LotusMedallion = ({ size = 44, className = "", style = {}, testid }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    className={className}
    data-testid={testid}
    style={style}
    aria-hidden="true"
    fill="none"
  >
    {Array.from({ length: 8 }).map((_, i) => (
      <ellipse
        key={i}
        cx="50"
        cy="26"
        rx="9"
        ry="22"
        fill="currentColor"
        opacity={i % 2 ? 0.55 : 0.85}
        transform={`rotate(${i * 45} 50 50)`}
      />
    ))}
    <circle cx="50" cy="50" r="7" fill="currentColor" />
  </svg>
);

// Sandakada pahana (moonstone) concentric arc motif — a decorative backdrop.
export const MoonstoneArc = ({ size = 320, className = "", style = {}, testid }) => (
  <svg
    width={size}
    height={size / 2}
    viewBox="0 0 320 160"
    className={className}
    data-testid={testid}
    style={style}
    aria-hidden="true"
    fill="none"
  >
    {[150, 118, 86, 54].map((r, i) => (
      <path
        key={r}
        d={`M${160 - r} 160 A ${r} ${r} 0 0 1 ${160 + r} 160`}
        stroke="currentColor"
        strokeWidth="2"
        opacity={0.18 + i * 0.06}
      />
    ))}
    {Array.from({ length: 13 }).map((_, i) => {
      const a = Math.PI * (i / 12);
      return (
        <line
          key={i}
          x1={160 - Math.cos(a) * 54}
          y1={160 - Math.sin(a) * 54}
          x2={160 - Math.cos(a) * 150}
          y2={160 - Math.sin(a) * 150}
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.12"
        />
      );
    })}
  </svg>
);

// A soft radial glow blob for layering warmth behind sections.
export const GlowBlob = ({ className = "", style = {} }) => (
  <div
    aria-hidden="true"
    className={className}
    style={{
      borderRadius: "50%",
      filter: "blur(60px)",
      background: "radial-gradient(circle, currentColor 0%, transparent 70%)",
      opacity: 0.25,
      ...style,
    }}
  />
);

// Swaranjaliya emblem — a lotus (offering / anjali) with a flame rising into
// three ascending swara (note) dots, ringed in fine liya-pata. Prestigious, no crown.
export const SwaranjaliyaMark = ({ size = 30, className = "", style = {}, testid }) => {
  const uid = useId().replace(/:/g, "");
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className={className} data-testid={testid} style={style} aria-hidden="true" fill="none">
      <defs>
        <linearGradient id={`sm-gold-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F4C430" />
          <stop offset="50%" stopColor="#FFF4C9" />
          <stop offset="100%" stopColor="#C08A1E" />
        </linearGradient>
      </defs>
      <g stroke={`url(#sm-gold-${uid})`} fill={`url(#sm-gold-${uid})`}>
        {/* rings */}
        <circle cx="32" cy="32" r="30" fill="none" strokeWidth="1.4" opacity="0.95" />
        <circle cx="32" cy="32" r="26.5" fill="none" strokeWidth="0.6" opacity="0.45" />
        {/* flame */}
        <path d="M32 11 C 35 16, 36.6 19, 34.6 23 C 33.5 25.2, 30.5 25.2, 29.4 23 C 27.4 19.3, 29.4 15.5, 32 11 Z" strokeWidth="0" />
        {/* ascending swara note dots */}
        <circle cx="40" cy="16.5" r="1.7" strokeWidth="0" />
        <circle cx="43.2" cy="12.4" r="1.3" strokeWidth="0" />
        <circle cx="45.6" cy="9" r="1" strokeWidth="0" />
        {/* lotus — center petal */}
        <path d="M32 27 C 36 33, 36 39, 32 43.5 C 28 39, 28 33, 32 27 Z" strokeWidth="0" />
        {/* inner side petals */}
        <path d="M32 43.5 C 26 41.5, 22 37.5, 21 31 C 26.5 32, 30 36, 32 43.5 Z" strokeWidth="0" opacity="0.85" />
        <path d="M32 43.5 C 38 41.5, 42 37.5, 43 31 C 37.5 32, 34 36, 32 43.5 Z" strokeWidth="0" opacity="0.85" />
        {/* outer petals */}
        <path d="M32 44.5 C 24 44.5, 17 41, 14 35 C 20.5 35, 27 38.5, 32 44.5 Z" strokeWidth="0" opacity="0.6" />
        <path d="M32 44.5 C 40 44.5, 47 41, 50 35 C 43.5 35, 37 38.5, 32 44.5 Z" strokeWidth="0" opacity="0.6" />
      </g>
    </svg>
  );
};
// Body inherits `currentColor`; the flame glows warm regardless of theme.
export const PahanaLamp = ({ size = 28, className = "", style = {}, testid }) => {
  const uid = useId().replace(/:/g, "");
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      data-testid={testid}
      style={style}
      aria-hidden="true"
      fill="none"
    >
      <defs>
        <radialGradient id={`flame-${uid}`} cx="50%" cy="38%" r="65%">
          <stop offset="0%" stopColor="#FFF3C4" />
          <stop offset="45%" stopColor="#FFC24B" />
          <stop offset="100%" stopColor="#F5721A" />
        </radialGradient>
        <radialGradient id={`halo-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFD873" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#FFD873" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* flame halo */}
      <circle cx="32" cy="15" r="13" fill={`url(#halo-${uid})`} />
      {/* flame */}
      <path d="M32 4 C 36 10, 38.5 13.5, 36 18.5 C 34.6 21.4, 29.4 21.4, 28 18.5 C 25.8 14, 29 10, 32 4 Z" fill={`url(#flame-${uid})`} />
      <path d="M32 10 C 33.6 12.4, 34.4 14.4, 33.2 16.6 C 32.5 18, 31.5 18, 30.8 16.6 C 29.8 14.6, 30.8 12.4, 32 10 Z" fill="#FFF7E0" opacity="0.9" />

      {/* oil bowl with two pointed spouts */}
      <path d="M9 25 Q 32 18 55 25 Q 32 31.5 9 25 Z" fill="currentColor" />
      <path d="M9 25 L 4 24.2 L 9 26.6 Z" fill="currentColor" />
      <path d="M55 25 L 60 24.2 L 55 26.6 Z" fill="currentColor" />
      <path d="M26 24 Q 32 21.5 38 24 Q 32 26.5 26 24 Z" fill="#00000022" />

      {/* stem with decorative knops */}
      <rect x="30" y="30" width="4" height="18" rx="1.4" fill="currentColor" />
      <ellipse cx="32" cy="33.5" rx="6" ry="2.4" fill="currentColor" />
      <ellipse cx="32" cy="41" rx="5" ry="2.1" fill="currentColor" />

      {/* tiered lotus base */}
      <ellipse cx="32" cy="49.5" rx="9" ry="3" fill="currentColor" />
      <ellipse cx="32" cy="55" rx="14" ry="4.2" fill="currentColor" />
      <path d="M18 55 Q 32 61 46 55 Q 32 58.5 18 55 Z" fill="#00000026" />
    </svg>
  );
};
